import React, { useEffect, useRef, useState } from "react";
import { Button, Collapse, Modal } from "react-bootstrap";
import { AppConfiguration, KeyCode, SupportRoutes } from "src/common";
import { Icon } from "src/components";
import _ from "lodash";
import add_folder_img from "src/assets/images/quick-start-menu/add-folder.svg";
import cloud_drive_img from "src/assets/images/quick-start-menu/cloud-drive.png";
import save_recovery_file_express_img from "src/assets/images/quick-start-menu/save-recovery-file-express-sm.svg";
import save_recovery_file_full_img from "src/assets/images/quick-start-menu/save-recovery-file-sm.svg";
import send_email_img from "src/assets/images/quick-start-menu/send-email.svg";
import share_folder_img from "src/assets/images/quick-start-menu/share-folder.svg";
import upgrade_img from "src/assets/images/quick-start-menu/upgrade.svg";
import upload_file_img from "src/assets/images/quick-start-menu/upload-file.svg";

type QuickStartItemButton = {
  href: string;
  label: string;
  variant: "primary" | "outline-primary";
};

type QuickStartItem = {
  action_buttons?: QuickStartItemButton[];
  description?: string;
  icon_class: string;
  image_url?: string;
  key: string;
  label: string;
};

/* Description: A collapsible Quick Start menu that overlays the left navigation.
  When expanded, it reveals a set of guided actions: sending an email, uploading a file,
  adding a folder, share folder, add device, save recovery file, take tour. Each item in the menu can be individually expanded to show a description,
  and optionally display an image in a gallery-style modal with arrow navigation. */
function QuickStartMenuComponent() {
  const is_express = AppConfiguration.buildForExpress();
  const [open, setOpen] = useState<boolean>(false);
  const [open_key, setOpenKey] = useState<string>();
  const [gallery_index, setGalleryIndex] = useState<number>(-1);
  const [fullscreen, setFullscreen] = useState<boolean>(false);
  const gallery_index_ref = useRef<number>(-1);
  const disable_click_ref = useRef<boolean>(false);
  const slide_ref = useRef<HTMLDivElement>(null);

  const items: QuickStartItem[] = [
    {
      key: "cloud_drive",
      icon_class: "icon-folder",
      label: "Cloud Drive",
      image_url: cloud_drive_img,
      action_buttons: [
        { href: SupportRoutes.preveil_support, label: "Get Help", variant: "outline-primary" },
        { href: SupportRoutes.cloud_drive_brief, label: "Learn More", variant: "primary" },
      ],
    },
    {
      key: "mail",
      icon_class: "icon-mail5",
      label: "Send an Email",
      description: "Send a secure PreVeil email by selecting the Compose button.",
      image_url: send_email_img,
    },
    {
      key: "upload",
      icon_class: "icon-upload",
      label: "Upload a File",
      description: "In Drive, click the upload menu button and browse to your file.",
      image_url: upload_file_img,
    },
    {
      key: "add_folder",
      icon_class: "icon-folder-plus2",
      label: "Add a Folder",
      description: "In Drive, click the add new folder button to add a folder to your content.",
      image_url: add_folder_img,
    },
    {
      key: "share_folder",
      icon_class: "icon-more2",
      label: "Share a Folder",
      description: "In Drive, click the more button in the right of your row to securely share a folder.",
      image_url: share_folder_img,
    },
    is_express ? {
      key: "upgrade",
      icon_class: "icon-arrow-up16",
      label: "Upgrade to Full PreVeil",
      description: "Upgrade to the PreVeil Desktop application for added features like Outlook, Gmail, and File Explorer integration.",
      image_url: upgrade_img,
    } : {
      key: "add_device",
      icon_class: "icon-mobile2",
      label: "Get the Mobile App",
      description: "Use the PreVeil application on your mobile device.",
    },
    {
      key: "save_recovery_file",
      icon_class: "icon-file-check",
      label: "Save the Recovery File",
      description: `In settings Account Recovery, select the ${is_express ? "Download Recovery File" : "Create Recovery File"} button.`,
      image_url: is_express ? save_recovery_file_express_img : save_recovery_file_full_img,
    }
  ];

  // Description: Only items with images are navigable in the gallery
  const gallery_items = items.filter((item) => !!item.image_url);
  const current_item = gallery_index >= 0 ? gallery_items[gallery_index] : undefined;
  const show_modal = gallery_index >= 0;

  // Description: Handle keyboard navigation for left and right arrows
  useEffect(() => {
    if (!show_modal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      e.key === KeyCode.ArrowLeft && handleNavigate(-1);
      e.key === KeyCode.ArrowRight && handleNavigate(1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [show_modal]);

  // Description: Sync ref and auto-expand the corresponding collapsible panel
  useEffect(() => {
    gallery_index_ref.current = gallery_index;
    if (current_item) {
      setOpenKey(current_item.key);
    }
  }, [gallery_index]);

  // Description: Open the gallery at the index of the given item key
  function openGallery(item_key: string) {
    const index = gallery_items.findIndex((item) => item.key === item_key);
    if (index >= 0) {
      setGalleryIndex(index);
    }
  }

  // Description: Close the gallery modal
  function closeGallery() {
    setGalleryIndex(-1);
    setFullscreen(false);
    disable_click_ref.current = false;
  }

  // Description: Navigate the gallery by direction (-1 for left, +1 for right)
  function handleNavigate(direction: number) {
    if (disable_click_ref.current) return;
    const current_index = gallery_index_ref.current;
    const new_index = current_index + direction;
    if (new_index < 0 || new_index >= gallery_items.length) return;
    disable_click_ref.current = true;
    const slide_out_class = direction > 0 ? "qs-slide-out-left" : "qs-slide-out-right";
    const slide_in_class = direction > 0 ? "qs-slide-in-right" : "qs-slide-in-left";
    // Slide out current content
    if (slide_ref.current) {
      slide_ref.current.classList.add(slide_out_class);
    }
    setTimeout(() => {
      setGalleryIndex(new_index);
      // Remove slide-out, add slide-in
      if (slide_ref.current) {
        slide_ref.current.classList.remove(slide_out_class);
        slide_ref.current.classList.add(slide_in_class);
      }
      setTimeout(() => {
        if (slide_ref.current) {
          slide_ref.current.classList.remove(slide_in_class);
        }
        disable_click_ref.current = false;
      }, 300);
    }, 300);
  }

  // Description: Toggle the collapsible panel for a given item key
  function toggleItem(key: string) {
    setOpenKey(open_key === key ? undefined : key);
  }

  // Description: Render the list of quick start menu items
  function displayMenuItems() {
    return <>
      {_.map(items, (item: QuickStartItem) => {
        return <div key={item.key}>
          <Button variant="quick-start" onClick={() => toggleItem(item.key)}>
            <span><Icon className={item.icon_class} />{item.label}</span>
            <Icon className={`${open_key === item.key ? "ficon-chevron-up" : "ficon-chevron-down"}`} />
          </Button>
          {open_key === item.key && <div className="sub-menu">
            {item.description && <p>{item.description}</p>}
            {item.key === "add_device" ? <a
              href={SupportRoutes.add_device}
              target="_blank"
              rel="noreferrer"
              className="btn btn-primary btn-sm mt-1"
            >
              <span><Icon className="icon-mobile"></Icon>Show me</span>
            </a> :
              <Button size="sm" onClick={() => openGallery(item.key)}>Show Me</Button>}
          </div>}
        </div>;
      })}
    </>;
  }

  return (
    <div className="quick-start-menu">
      <Button onClick={() => setOpen(!open)} variant="quick-start" className="main-button">
        PreVeil Quick Start <Icon className={`${open ? "ficon-chevron-down" : "ficon-chevron-up"}`} />
      </Button>
      <Collapse in={open}>
        <div className={`${open ? "border-top" : ""}`}>
          {open && <>
            {displayMenuItems()}
            <a
              href={SupportRoutes.take_a_tour}
              target="_blank"
              rel="noreferrer"
              className="btn btn-quick-start"
            >
              <span><Icon className="icon-question3" /> Take a Tour</span>
            </a>
          </>}
        </div>
      </Collapse>
      {show_modal && !!current_item &&
        <Modal
          size="xl"
          centered
          show={show_modal}
          fullscreen={fullscreen || undefined}
          className="quick-start-gallery-modal"
          dialogClassName={`${fullscreen ? "fullscreen" : ""}`}
          scrollable={true}
          onHide={closeGallery} >
          <Modal.Header closeButton className="px-3 pb-2" closeVariant="white">
            <span className="title-span">
              <Modal.Title>{current_item.label}</Modal.Title>
              <span className="media-count">
                {gallery_index + 1} <span className="of">of</span> {gallery_items.length}
              </span>
            </span>
            <Button
              variant="transparent"
              className="btn-icon ms-auto maximize"
              size="sm"
              onClick={() => setFullscreen(!fullscreen)}
            >
              <span>
                <Icon className={fullscreen ? "ficon-minimize-2" : "ficon-maximize-2"} />
              </span>
            </Button>
          </Modal.Header>
          <Modal.Body className="px-0 py-2">
            <div className="qs-gallery-container">
              <button
                onMouseDown={(e) => e.preventDefault()}
                className={`qs-gallery-arrow me-auto ${gallery_index === 0 ? "invisible" : ""}`}
                onClick={() => handleNavigate(-1)}
              >
                <Icon className="arrow-icon ficon-chevron-left" />
              </button>
              <div className="qs-gallery-center">
                <div className="qs-gallery-content" ref={slide_ref}>
                  <img
                    className="quick-start-img"
                    src={current_item.image_url}
                    alt={current_item.label}
                  />
                </div>
                {!!current_item.action_buttons?.length &&
                  <div className="qs-gallery-actions">
                    {current_item.action_buttons.map((btn: QuickStartItemButton) =>
                      <Button
                        key={btn.label}
                        size="sm"
                        variant={btn.variant}
                        href={btn.href}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <span>{btn.label}</span>
                      </Button>
                    )}
                  </div>
                }
              </div>
              <button
                onMouseDown={(e) => e.preventDefault()}
                className={`qs-gallery-arrow ms-auto ${gallery_index >= gallery_items.length - 1 ? "invisible" : ""}`}
                onClick={() => handleNavigate(1)} >
                <Icon className="arrow-icon ficon-chevron-right" />
              </button>
            </div>
            {current_item.description && <div className="qs-gallery-caption">
              <p>{current_item.description}</p>
            </div>}
          </Modal.Body>
        </Modal>
      }
    </div>
  );
}

export default React.memo(QuickStartMenuComponent);
